import { formatUnits, parseUnits, type Address } from "viem";

export function formatTokenAmount(
  amount: bigint,
  decimals: number = 18,
  displayDecimals: number = 4
): string {
  const formatted = formatUnits(amount, decimals);
  const num = parseFloat(formatted);

  if (num === 0) return "0";
  if (num < 0.0001) return "<0.0001";
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + "M";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(2) + "K";
  }

  return num.toFixed(displayDecimals);
}

export function formatCurrency(
  amount: bigint,
  decimals: number = 18,
  displayDecimals: number = 2
): string {
  const num = parseFloat(formatUnits(amount, decimals));
  if (num >= 1_000_000_000) return "$" + (num / 1_000_000_000).toFixed(2) + "B";
  if (num >= 1_000_000) return "$" + (num / 1_000_000).toFixed(2) + "M";
  if (num >= 1_000) return "$" + (num / 1_000).toFixed(2) + "K";
  return "$" + num.toFixed(displayDecimals);
}

export function formatJasmy(amount: bigint, displayDecimals: number = 4): string {
  return formatTokenAmount(amount, 18, displayDecimals) + " JASMY";
}

export function parseTokenAmount(amount: string, decimals: number = 18): bigint {
  if (!amount || amount === "" || amount === ".") return 0n;
  try {
    return parseUnits(amount, decimals);
  } catch {
    return 0n;
  }
}

export function shortenAddress(address: Address | string, chars: number = 4): string {
  if (!address) return "";
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
}

export function getDeadline(minutes: number = 20): bigint {
  return BigInt(Math.floor(Date.now() / 1000) + minutes * 60);
}

export function calculateSlippage(amount: bigint, slippageBps: number): bigint {
  return (amount * BigInt(10000 - slippageBps)) / 10000n;
}

export function bpsToPercent(bps: number | bigint): string {
  const n = typeof bps === "bigint" ? Number(bps) : bps;
  return (n / 100).toFixed(2) + "%";
}

export function formatProgress(progressBps: bigint): number {
  // Contract returns progress in BPS (0-10000), convert to percentage (0-100)
  return Math.min(Number(progressBps) / 100, 100);
}

export function timeAgo(timestamp: bigint): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - Number(timestamp);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
}

export function isValidAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

export function explorerUrl(type: "tx" | "address" | "token", value: string): string {
  const base = "https://explorer.jasmyscan.net";
  switch (type) {
    case "tx":
      return `${base}/tx/${value}`;
    case "address":
      return `${base}/address/${value}`;
    case "token":
      return `${base}/token/${value}`;
  }
}

export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(" ");
}
